import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React from 'react'
import styled from 'styled-components'

import AnimateText from '../../helpers/animateText'
import Parallax from '../../helpers/parallax'
import { ExtendedH6, H2 } from '../Typography/Typography'
import Link from '../atoms/link'
import theme from '../atoms/theme'
import Button from '../molecules/button'
import { renderCategories } from './heroCarousel'

const { colours, breakpoints } = theme

type SpotlightProp = {
  preTitle: string | null
  title: string | null
  slug: string | null
  image: Queries.ContentfulAsset | null
  leadingParagraph: string
  categoryTitle?: string
  categorySlug?: string
  categories?: object[] | object
  type: 'event' | 'page' | 'exhibition' | 'experience'
  isFlipped?: boolean
  focalRatio?: {
    x: number
    y: number
  }
}

const SpotlightContainer = styled.div`
  background-color: ${colours.dark};
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 0;

  @media only screen and ${breakpoints.toNormalScreen} {
    flex-direction: column;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    height: 46.4rem;
    margin-bottom: 14rem;
    flex-direction: ${(props): string => (props.isFlipped ? 'row-reverse' : 'row')};
  }
`

const ImageContainer = styled.div`
  height: 100%;
  width: 100%;

  @media only screen and ${breakpoints.toNormalScreen} {
    height: 40vh;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    flex-basis: 66.6%;
  }

  @media only screen and (min-width: 1440px) {
    flex-basis: calc(50% + 22.8rem);
  }
`

const FillImage = styled(Parallax)`
  width: 100%;
  height: auto;

  @media only screen and ${breakpoints.fromNormalScreen} {
    height: 100%;
  }
`

const SpotlightContentContainer = styled.div`
  color: ${colours.light};
  padding-left: 2rem;
  padding-right: 2rem;
  width: 100%;

  @media only screen and ${breakpoints.toNormalScreen} {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }

  @media only screen and ${breakpoints.fromNormalScreen} {
    padding-left: ${(props): string => (props.isFlipped ? '2.4rem' : '4rem')};
    padding-right: 2.4rem;
    flex-basis: calc(33.3%);
  }

  @media only screen and (min-width: 1440px) {
    flex-basis: 47.2rem;
    padding-left: ${(props): string => (props.isFlipped ? '2.4rem' : '4rem')};
  }
`

const CategoryLink = styled(Link)`
  color: ${colours.primary};
  text-decoration: none;

  &:hover {
    color: ${colours.light};
  }
`

const Spotlight = (props: SpotlightProp): JSX.Element => {
  const {
    preTitle,
    title,
    image,
    leadingParagraph,
    type,
    slug,
    isFlipped,
    categories,
    focalRatio,
  } = props

  const eventType = {
    title: type,
    slug: type,
  }

  return (
    <SpotlightContainer
      data-test-id='spotlight'
      isFlipped={isFlipped}
      data-sal='slide-up'
      data-sal-duration='1000'
    >
      {image && (
        <ImageContainer className='image'>
          <FillImage image={getImage(image)} focalRatio={focalRatio} />
        </ImageContainer>
      )}
      <SpotlightContentContainer isFlipped={isFlipped}>
        {categories &&
          Array.isArray(categories) &&
          renderCategories(eventType, categories)}
        {categories && !Array.isArray(categories) && (
          <ExtendedH6>
            <CategoryLink className='category' to={`${categories.slug}/`}>
              {categories.title}
            </CategoryLink>
          </ExtendedH6>
        )}
        <H2 className='headline'>
          {preTitle && (
            <div>
              <AnimateText text={preTitle} />
            </div>
          )}
          {title && <AnimateText text={title} />}
        </H2>
        <p className='description'>{leadingParagraph}</p>
        {type === 'page' ? (
          <Button className='learn-more' to={`${slug}/`}>
            View more
          </Button>
        ) : (
          <Button className='learn-more' to={`${type}s/${slug}/`}>
            View more
          </Button>
        )}
      </SpotlightContentContainer>
    </SpotlightContainer>
  )
}

export default Spotlight
